import React, { useState, useEffect } from "react";
import BotonManualHeader from "../../components/BotonManualHeader";
import {
  Container, Card, Form, Button, Table, Modal, Row, Col, Spinner, Badge,
} from "react-bootstrap";
import Swal from "sweetalert2";
import {
  getFirestore, collection, getDocs, addDoc, doc, updateDoc, deleteDoc, query, where,
} from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import { GrEdit } from "react-icons/gr";
import { RiDeleteBin6Fill } from "react-icons/ri";
import { FaPlus, FaHistory, FaSearch, FaFileImport, FaDownload, FaHammer, FaExclamationTriangle, FaBoxes, FaTags, FaWrench } from "react-icons/fa";
import { BiBox, BiPlus, BiHistory, BiEdit, BiTrash, BiSearch, BiDownload, BiFile, BiWrench } from "react-icons/bi";
import * as XLSX from "xlsx";

const db = getFirestore(firebaseApp);

// Categorías de herramientas (ajusta según tus necesidades)
const CATEGORIAS_HERRAMIENTAS = [
  "manual",
  "electrica",
  "neumatica",
  "medicion",
  "corte",
  "otro"
];

function CatalogoHerramientas({ usuario }) {
  const ruc = usuario.ruc;
  const [herramientas, setHerramientas] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [filtroCategoria, setFiltroCategoria] = useState("");
  const [modoEdicion, setModoEdicion] = useState(false);
  const [herramientaEditada, setHerramientaEditada] = useState(null);

  const [formData, setFormData] = useState({
    nombre: "",
    descripcion: "",
    categoria: "",
    codigo: "",
    marca: "",
    modelo: "",
    estado: "Disponible",
    cantidad: 1,
  });

  // Estadísticas
  const [stats, setStats] = useState({
    total: 0,
    disponibles: 0,
    prestados: 0,
    mantenimiento: 0
  });

  useEffect(() => {
    if (ruc) cargarHerramientas();
  }, [ruc]);

  const cargarHerramientas = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, "herramientas_catalogo"), where("ruc_empresa", "==", ruc));
      const querySnapshot = await getDocs(q);
      const herramientasData = [];
      let disp = 0, pres = 0, mant = 0;

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        herramientasData.push({ id: doc.id, ...data });
        
        if (data.estado === "Disponible") disp += (parseInt(data.cantidad) || 0);
        else if (data.estado === "Prestado") pres += (parseInt(data.cantidad) || 0);
        else if (data.estado === "Mantenimiento" || data.estado === "Baja") mant += (parseInt(data.cantidad) || 0);
      });

      setHerramientas(herramientasData);
      setStats({
        total: herramientasData.length,
        disponibles: disp,
        prestados: pres,
        mantenimiento: mant
      });
    } catch (error) {
      console.error("Error al cargar herramientas:", error);
      Swal.fire({ title: "Error", text: "No se pudieron cargar las herramientas", icon: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (!CATEGORIAS_HERRAMIENTAS.includes(formData.categoria)) {
        Swal.fire({ title: "Error", text: "La categoría seleccionada no es válida.", icon: "error" });
        setLoading(false);
        return;
      }

      const dataToSave = {
        ...formData,
        cantidad: parseInt(formData.cantidad),
        ruc_empresa: ruc,
      };

      if (modoEdicion) {
        const docRef = doc(db, "herramientas_catalogo", herramientaEditada.id);
        await updateDoc(docRef, { ...dataToSave, fechaActualizacion: new Date() });
      } else {
        await addDoc(collection(db, "herramientas_catalogo"), { ...dataToSave, fechaRegistro: new Date(), activo: true });
      }
      await cargarHerramientas();
      limpiarFormulario();
      setShowModal(false);
      Swal.fire({ title: "¡Éxito!", text: modoEdicion ? "Herramienta actualizada correctamente" : "Herramienta agregada correctamente", icon: "success" });
    } catch (error) {
      Swal.fire({ title: "Error", text: error.message, icon: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (herramienta) => {
    setModoEdicion(true);
    setHerramientaEditada(herramienta);
    setFormData({
      nombre: herramienta.nombre || "",
      descripcion: herramienta.descripcion || "",
      categoria: herramienta.categoria || "",
      codigo: herramienta.codigo || "",
      marca: herramienta.marca || "",
      modelo: herramienta.modelo || "",
      estado: herramienta.estado || "Disponible",
      cantidad: herramienta.cantidad || 1,
    });
    setShowModal(true);
  };

  const handleEliminar = async (id) => {
    try {
      const result = await Swal.fire({
        title: "¿Estás seguro?",
        text: "Esta acción no se puede deshacer",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#f97316",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });
      if (result.isConfirmed) {
        await deleteDoc(doc(db, "herramientas_catalogo", id));
        await cargarHerramientas();
        Swal.fire("¡Eliminado!", "La herramienta ha sido eliminada.", "success");
      }
    } catch (error) {
      Swal.fire({ title: "Error", text: "No se pudo eliminar la herramienta", icon: "error" });
    }
  };

  const limpiarFormulario = () => {
    setFormData({
      nombre: "",
      descripcion: "",
      categoria: "",
      codigo: "",
      marca: "",
      modelo: "",
      estado: "Disponible",
      cantidad: 1,
    });
    setModoEdicion(false);
    setHerramientaEditada(null);
  };

  const handleDescargarPlantilla = () => {
    // 1. Hoja de Plantilla con Ejemplo
    const data = [
      ["codigo", "nombre", "categoria", "marca", "modelo", "estado", "cantidad", "descripcion"],
      ["H-001", "TALADRO DE PERCUSIÓN", "electrica", "DEWALT", "DCD771C2", "Disponible", "2", "Taladro con 2 baterías y cargador"],
      ["H-002", "MARTILLO DE CARPINTERO", "manual", "STANLEY", "51-616", "Disponible", "5", "Martillo de acero alto carbono"]
    ];
    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "PlantillaHerramientas");

    // 2. Hoja de Ayuda (Instrucciones)
    const helpData = [
      ["COLUMNA", "DESCRIPCIÓN", "OBLIGATORIO", "VALORES RECOMENDADOS / EJEMPLO"],
      ["codigo", "Código único de la herramienta", "SI", "H-001"],
      ["nombre", "Nombre de la herramienta", "SI", "LLAVE INGLESA"],
      ["categoria", "Categoría del sistema (Ver abajo)", "SI", "manual, electrica, neumatica, etc."],
      ["marca", "Marca del fabricante", "NO", "STANLEY"],
      ["modelo", "Modelo específico", "NO", "10 Pulgadas"],
      ["estado", "Estado inicial", "SI", "Disponible"],
      ["cantidad", "Cantidad de unidades físicas", "SI", "5"],
      ["descripcion", "Detalles adicionales", "NO", "Mango antideslizante"],
      ["", "", "", ""],
      ["CATEGORÍAS DE HERRAMIENTAS (Escribir exactamente así):", "", "", ""],
      [CATEGORIAS_HERRAMIENTAS.join(", "), "", "", ""],
      ["", "", "", ""],
      ["NOTA:", "La cantidad debe ser un número entero mayor a 0.", "", ""]
    ];
    const wsHelp = XLSX.utils.aoa_to_sheet(helpData);
    XLSX.utils.book_append_sheet(wb, wsHelp, "AYUDA_INSTRUCCIONES");

    XLSX.writeFile(wb, "plantilla_herramientas_con_guia.xlsx");
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet);

      const nuevosHerramientas = rows.map((row) => ({
        codigo: row.codigo || row.Código || "",
        nombre: row.nombre || row.Nombre || "",
        categoria: (row.categoria || row.Categoría || "").toLowerCase(),
        marca: row.marca || row.Marca || "",
        modelo: row.modelo || row.Modelo || "",
        estado: row.estado || row.Estado || "Disponible",
        cantidad: parseInt(row.cantidad || row.Cantidad || 1),
        descripcion: row.descripcion || row.Descripción || "",
        fechaRegistro: new Date(),
        activo: true,
        ruc_empresa: ruc,
      }));

      const validos = nuevosHerramientas.filter(
        h =>
          h.codigo &&
          h.nombre &&
          h.categoria &&
          CATEGORIAS_HERRAMIENTAS.includes(h.categoria)
      );

      for (const h of validos) {
        await addDoc(collection(db, "herramientas_catalogo"), h);
      }

      await cargarHerramientas();
      Swal.fire("¡Éxito!", `Se importaron ${validos.length} herramientas correctamente.`, "success");
    } catch (error) {
      Swal.fire("Error", "No se pudo importar el archivo.", "error");
    } finally {
      setLoading(false);
      e.target.value = "";
    }
  };

  const herramientasFiltradas = herramientas.filter((h) => {
    const cumpleBusqueda =
      h.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
      (h.codigo || "").toString().includes(busqueda);
    const cumpleCategoria =
      !filtroCategoria || h.categoria === filtroCategoria;
    return cumpleBusqueda && cumpleCategoria;
  });

  const getBadgeColor = (estado) => {
    switch (estado) {
      case "Disponible": return "success";
      case "Prestado": return "warning";
      case "Mantenimiento": return "info";
      case "Baja": return "danger";
      default: return "secondary";
    }
  };

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        padding: "20px 0",
      }}
    >
      <Container fluid className="px-lg-5 px-3">
        <Card className="shadow-lg border-0" style={{ borderRadius: "15px", overflow: "hidden" }}>
          <Card.Header
            className="text-white py-4 position-relative"
            style={{
              background: "linear-gradient(135deg, #f97316 0%, #fb923c 100%)",
              borderBottom: "3px solid #ea580c",
            }}
          >
            <div className="position-absolute end-0 top-50 translate-middle-y me-4" style={{ zIndex: 10 }}>
              <BotonManualHeader usuario={usuario} />
            </div>
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                <FaHammer className="me-3" />
                Catálogo de Herramientas
              </h2>
              <p className="mb-0 opacity-75">Administración de Herramientas Reutilizables</p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {/* Barra superior con acciones */}
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col md={1}>
                    <div
                      className="rounded-3 p-3 text-center"
                      style={{
                        background: "linear-gradient(135deg, #f97316 0%, #fb923c 100%)",
                        color: "white",
                      }}
                    >
                      <BiWrench size={24} />
                    </div>
                  </Col>
                  <Col md={7}>
                    <h5 className="mb-1 fw-semibold">Acciones del Catálogo</h5>
                    <p className="text-muted mb-0 small">
                      Agregue nuevas herramientas o importe inventario desde Excel
                    </p>
                  </Col>
                  <Col md={4} className="text-end d-flex gap-2 justify-content-end">
                    <Button
                      variant="light"
                      onClick={() => {
                        limpiarFormulario();
                        setShowModal(true);
                      }}
                      className="d-flex align-items-center gap-2"
                      style={{ borderRadius: "10px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}
                    >
                      <FaPlus size="16px" /> Nueva Herramienta
                    </Button>
                    <Form.Label
                      className="btn btn-light mb-0 d-flex align-items-center gap-2"
                      style={{ borderRadius: "10px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)", cursor: "pointer" }}
                    >
                      <FaFileImport size="16px" /> Importar
                      <Form.Control
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        onChange={handleImportExcel}
                        style={{ display: "none" }}
                      />
                    </Form.Label>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Fila de KPIs */}
            <Row className="mb-4 g-3">
              <Col md={3}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                  <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #f97316 0%, #fb923c 100%)", color: "white", borderRadius: "15px" }}>
                    <h4 className="fw-bold mb-1">{stats.total}</h4>
                    <p className="mb-0 opacity-90 small">Tipos de Herramientas</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                  <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #059669 0%, #10b981 100%)", color: "white", borderRadius: "15px" }}>
                    <h4 className="fw-bold mb-1">{stats.disponibles}</h4>
                    <p className="mb-0 opacity-90 small">Stock Disponible (Unid)</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                  <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)", color: "white", borderRadius: "15px" }}>
                    <h4 className="fw-bold mb-1">{stats.prestados}</h4>
                    <p className="mb-0 opacity-90 small">En Préstamo (Unid)</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                  <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)", color: "white", borderRadius: "15px" }}>
                    <h4 className="fw-bold mb-1">{stats.mantenimiento}</h4>
                    <p className="mb-0 opacity-90 small">Baja / Mantenim.</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Filtros */}
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
              <Card.Body className="p-3">
                <Row className="g-3">
                  <Col md={5}>
                    <div className="position-relative">
                      <FaSearch className="position-absolute" style={{ left: "15px", top: "50%", transform: "translateY(-50%)", color: "#6c757d" }} />
                      <Form.Control
                        type="text"
                        placeholder="Buscar por nombre o código..."
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        className="ps-5 form-control-lg"
                        style={{ borderRadius: "10px", border: "2px solid #e9ecef" }}
                      />
                    </div>
                  </Col>
                  <Col md={4}>
                    <Form.Select
                      value={filtroCategoria}
                      onChange={(e) => setFiltroCategoria(e.target.value)}
                      className="form-control-lg"
                      style={{ borderRadius: "10px", border: "2px solid #e9ecef" }}
                    >
                      <option value="">Todas las categorías</option>
                      {CATEGORIAS_HERRAMIENTAS.map(cat => <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>)}
                    </Form.Select>
                  </Col>
                  <Col md={3} className="d-flex justify-content-end align-items-center">
                    <Button
                      variant="outline-warning"
                      onClick={handleDescargarPlantilla}
                      className="d-flex align-items-center gap-2 py-2 px-3 fw-bold w-100"
                      style={{ borderRadius: "10px", border: "2px solid #f97316" }}
                    >
                      <FaDownload /> Plantilla Excel
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Tabla */}
            <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: "15px", overflow: "hidden" }}>
              <Card.Header
                className="text-white py-3"
                style={{
                  background: "linear-gradient(135deg, #f97316 0%, #fb923c 100%)",
                  border: "none"
                }}
              >
                <div className="d-flex align-items-center">
                  <FaWrench className="me-2" />
                  <div className="flex-grow-1">
                    <h5 className="mb-0 fw-semibold">Inventario de Herramientas</h5>
                    <small className="opacity-75">Control detallado de herramientas manuales y eléctricas</small>
                  </div>
                  <Badge bg="light" text="dark">
                    {herramientasFiltradas.length} Registros
                  </Badge>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive" style={{ maxHeight: "600px" }}>
                  <Table hover className="align-middle mb-0" style={{ fontSize: "0.9rem" }}>
                    <thead style={{ backgroundColor: "#f8f9fa", position: "sticky", top: 0, zIndex: 10 }}>
                      <tr>
                        <th className="py-3 px-3 border-0 text-secondary fw-semibold">Código</th>
                        <th className="py-3 px-3 border-0 text-secondary fw-semibold">Herramienta</th>
                        <th className="py-3 px-3 border-0 text-secondary fw-semibold text-center">Categoría</th>
                        <th className="py-3 px-3 border-0 text-secondary fw-semibold">Marca/Modelo</th>
                        <th className="py-3 px-3 border-0 text-secondary fw-semibold text-center">Stock</th>
                        <th className="py-3 px-3 border-0 text-secondary fw-semibold text-center">Estado</th>
                        <th className="py-3 px-3 border-0 text-secondary fw-semibold text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {herramientasFiltradas.length === 0 ? (
                        <tr><td colSpan="7" className="text-center py-5 text-muted">No se encontraron herramientas.</td></tr>
                      ) : (
                        herramientasFiltradas.map((h) => (
                          <tr key={h.id}>
                            <td className="py-3 px-3 fw-bold">{h.codigo}</td>
                            <td className="py-3 px-3">
                              <div className="fw-bold">{h.nombre}</div>
                              <small className="text-muted">{h.descripcion?.substring(0, 50)}</small>
                            </td>
                            <td className="py-3 px-3 text-center text-capitalize">
                              <Badge bg="info" className="fw-normal">{h.categoria}</Badge>
                            </td>
                            <td className="py-3 px-3">{h.marca} / {h.modelo}</td>
                            <td className="py-3 px-3 text-center">
                              <span className="fw-bold">{h.cantidad}</span>
                            </td>
                            <td className="py-3 px-3 text-center">
                              <Badge bg={getBadgeColor(h.estado)} pill style={{ fontSize: "0.85rem" }}>
                                {h.estado}
                              </Badge>
                            </td>
                            <td className="py-3 px-3">
                              <div className="d-flex gap-1 justify-content-center">
                                <Button
                                  variant="outline-warning"
                                  size="sm"
                                  onClick={() => handleEditar(h)}
                                  style={{ borderRadius: "6px" }}
                                >
                                  <BiEdit size="14px" />
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleEliminar(h.id)}
                                  style={{ borderRadius: "6px" }}
                                >
                                  <BiTrash size="14px" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Card.Body>
        </Card>
      </Container>

      {/* Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold d-flex align-items-center text-dark">
            <div
              className="rounded-circle p-2 me-3 d-flex align-items-center justify-content-center"
              style={{ background: "rgba(249, 115, 22, 0.1)", color: "#f97316" }}
            >
              <FaHammer size={20} />
            </div>
            {modoEdicion ? "Editar Herramienta" : "Nueva Herramienta"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Form onSubmit={handleSubmit}>
            <Row className="g-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-bold">Código</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.codigo}
                    onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
                    required
                    className="form-control-lg"
                  />
                </Form.Group>
              </Col>
              <Col md={8}>
                <Form.Group>
                  <Form.Label className="fw-bold">Nombre de la Herramienta</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                    required
                    className="form-control-lg"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">Categoría</Form.Label>
                  <Form.Select
                    value={formData.categoria}
                    onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                    required
                    className="form-control-lg"
                  >
                    <option value="">Seleccione...</option>
                    {CATEGORIAS_HERRAMIENTAS.map(cat => <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="fw-bold">Cantidad</Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    value={formData.cantidad}
                    onChange={(e) => setFormData({ ...formData, cantidad: e.target.value })}
                    required
                    className="form-control-lg"
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="fw-bold">Estado</Form.Label>
                  <Form.Select
                    value={formData.estado}
                    onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                    required
                    className="form-control-lg"
                  >
                    <option value="Disponible">Disponible</option>
                    <option value="Prestado">Prestado</option>
                    <option value="Mantenimiento">Mantenimiento</option>
                    <option value="Baja">Baja</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">Marca</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.marca}
                    onChange={(e) => setFormData({ ...formData, marca: e.target.value })}
                    className="form-control-lg"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">Modelo</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.modelo}
                    onChange={(e) => setFormData({ ...formData, modelo: e.target.value })}
                    className="form-control-lg"
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-bold">Descripción / Notas</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={formData.descripcion}
                    onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                    className="form-control-lg"
                  />
                </Form.Group>
              </Col>
            </Row>
            <div className="d-flex gap-2 justify-content-end mt-4">
              <Button variant="light" size="lg" onClick={() => setShowModal(false)} className="px-4">Cancelar</Button>
              <Button
                variant="primary"
                type="submit"
                disabled={loading}
                size="lg"
                className="px-4 text-white fw-bold"
                style={{ background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)", border: "none", borderRadius: "10px" }}
              >
                {loading ? "Guardando..." : (modoEdicion ? "Actualizar Herramienta" : "Guardar Herramienta")}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default CatalogoHerramientas;